import type { JSX } from 'react';
import { EmptyState } from './EmptyState';
import styles from './HeatmapOverlay.module.scss';
import type { UsabilityHeatmapPoint } from '../api/usability.graphql';

const BUCKET_SIZE = 5;
const GRID_CELLS = 100 / BUCKET_SIZE;

/**
 * OS-1266 — heatmap "abstracto" por bucket de coordenadas (no hay screenshot real de cada
 * página tenant todavía, eso queda fuera de alcance de esta épica). Un grid por página con
 * intensidad proporcional al máximo de interacciones observado en esa página.
 */
export function HeatmapOverlay({ points }: { points: UsabilityHeatmapPoint[] }): JSX.Element {
  if (points.length === 0) {
    return <EmptyState message="Sin datos de heatmap para el rango seleccionado" />;
  }

  const byPage = groupByPage(points);

  return (
    <div className={styles.pages}>
      {[...byPage.entries()].map(([page, pagePoints]) => (
        <div key={page} className={styles.pageBlock}>
          <div className={styles.pageTitle}>{page}</div>
          <HeatmapGrid points={pagePoints} />
        </div>
      ))}
    </div>
  );
}

function HeatmapGrid({ points }: { points: UsabilityHeatmapPoint[] }): JSX.Element {
  const cellIntensity = new Map<string, number>();
  let max = 0;

  for (const point of points) {
    if (
      point.xBucket === null ||
      point.xBucket === undefined ||
      point.yBucket === null ||
      point.yBucket === undefined
    ) {
      continue;
    }
    const key = `${point.xBucket}:${point.yBucket}`;
    const value = (cellIntensity.get(key) ?? 0) + point.interactions;
    cellIntensity.set(key, value);
    max = Math.max(max, value);
  }

  const cells = [];
  for (let y = 0; y < GRID_CELLS; y++) {
    for (let x = 0; x < GRID_CELLS; x++) {
      const value = cellIntensity.get(`${x * BUCKET_SIZE}:${y * BUCKET_SIZE}`) ?? 0;
      const opacity = max > 0 ? value / max : 0;
      cells.push(
        <div
          key={`${x}-${y}`}
          className={styles.cell}
          style={{ background: `rgba(233, 30, 99, ${opacity})` }}
          title={value > 0 ? `${value} interacciones` : undefined}
        />,
      );
    }
  }

  return <div className={styles.grid}>{cells}</div>;
}

function groupByPage(points: UsabilityHeatmapPoint[]): Map<string, UsabilityHeatmapPoint[]> {
  const map = new Map<string, UsabilityHeatmapPoint[]>();
  for (const point of points) {
    const list = map.get(point.page) ?? [];
    list.push(point);
    map.set(point.page, list);
  }
  return map;
}
